/**
 * Puerto para abstracción de almacenamiento de tokens
 *
 * Permite inyectar diferentes implementaciones:
 * - Web: localStorage
 * - Mobile: Capacitor Secure Storage
 * - Test: In-memory storage
 */
export interface TokenStorage {
  /**
   * Obtiene el token de acceso
   */
  getToken(): string | null

  /**
   * Guarda el token de acceso
   */
  setToken(token: string): void

  /**
   * Elimina el token de acceso
   */
  removeToken(): void

  /**
   * Obtiene el refresh token
   */
  getRefreshToken(): string | null

  /**
   * Guarda el refresh token
   */
  setRefreshToken(token: string): void

  /**
   * Elimina el refresh token
   */
  removeRefreshToken(): void

  /**
   * Obtiene los datos del usuario almacenados
   */
  getUser<T = unknown>(): T | null

  /**
   * Guarda los datos del usuario
   */
  setUser<T = unknown>(user: T): void

  /**
   * Elimina los datos del usuario
   */
  removeUser(): void

  /**
   * Limpia todo el almacenamiento de autenticación
   */
  clearAll(): void
}

/**
 * Claves de almacenamiento estándar
 */
export const STORAGE_KEYS = {
  TOKEN: 'pepi_token',
  REFRESH_TOKEN: 'pepi_refresh_token',
  USER: 'pepi_user'
} as const

/**
 * Implementación para localStorage (Web)
 */
export class LocalStorageTokenStorage implements TokenStorage {
  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN)
  }

  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token)
  }

  removeToken(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN)
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token)
  }

  removeRefreshToken(): void {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  }

  getUser<T = unknown>(): T | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER)
    if (!userData) return null
    try {
      return JSON.parse(userData) as T
    } catch {
      return null
    }
  }

  setUser<T = unknown>(user: T): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
  }

  removeUser(): void {
    localStorage.removeItem(STORAGE_KEYS.USER)
  }

  clearAll(): void {
    this.removeToken()
    this.removeRefreshToken()
    this.removeUser()
  }
}

/**
 * Implementación in-memory para testing
 */
export class InMemoryTokenStorage implements TokenStorage {
  private store = new Map<string, string>()

  getToken(): string | null {
    return this.store.get(STORAGE_KEYS.TOKEN) ?? null
  }

  setToken(token: string): void {
    this.store.set(STORAGE_KEYS.TOKEN, token)
  }

  removeToken(): void {
    this.store.delete(STORAGE_KEYS.TOKEN)
  }

  getRefreshToken(): string | null {
    return this.store.get(STORAGE_KEYS.REFRESH_TOKEN) ?? null
  }

  setRefreshToken(token: string): void {
    this.store.set(STORAGE_KEYS.REFRESH_TOKEN, token)
  }

  removeRefreshToken(): void {
    this.store.delete(STORAGE_KEYS.REFRESH_TOKEN)
  }

  getUser<T = unknown>(): T | null {
    const userData = this.store.get(STORAGE_KEYS.USER)
    if (!userData) return null
    try {
      return JSON.parse(userData) as T
    } catch {
      return null
    }
  }

  setUser<T = unknown>(user: T): void {
    this.store.set(STORAGE_KEYS.USER, JSON.stringify(user))
  }

  removeUser(): void {
    this.store.delete(STORAGE_KEYS.USER)
  }

  clearAll(): void {
    this.store.clear()
  }
}
